import {fmtMDY} from './dateUtils.js';

// Record-page sequence navigation — route-state contract + neighbor math.
//
// A record page (cattle.animal, sheep.animal, …) shows Previous/Next controls
// ONLY when it was opened from a list whose visible order was handed through
// react-router location state. Direct URL opens, notification / deep-link
// opens, and related-record click-throughs (cow-to-cow, sheep-to-sheep) carry
// no sequence, so the controls stay hidden.
//
// The list passes its ordered, visible rows; we keep a minimal {id, tag, label?}
// projection (small enough to live in history state) under
// location.state.recordSeq. Prev/Next carry the SAME sequence forward.

// Project an array of record rows down to the minimal shape stored in route
// state: {id, tag, label?}. Rows without an id are dropped. `label` is an
// optional precomputed display string for records without a tag (daily
// reports, weigh-in sessions). Accepts rows already in this shape (used when
// carrying the sequence forward).

// An entry's `id` is deliberately `unknown`.
//
// labeledSeqItems reads it out of a CALLER-NAMED field — `idField` is 'slug'
// for the equipment routes and 'id' everywhere else — so this module genuinely
// cannot know its type. Narrowing it to `string | number` would either be a
// claim nothing here can check, or would mean DROPPING rows the current code
// keeps. Nothing in this file does more than `String()` it for comparison and
// hand it straight back, so `unknown` costs the module nothing and puts the
// obligation on the one caller that knows what its own ids are.
export type RecordSeqId = unknown;

// A source row handed to any of the builders below. Fields are read by name and
// some of those names come from the caller, so per-field `unknown` is the whole
// truth about a row at this boundary.
export type SeqSourceRow = {[field: string]: unknown};

// The minimal projection stored under location.state.recordSeq.
export type RecordSeqEntry = {
  id: RecordSeqId;
  tag: unknown;
  label?: unknown;
};

// A pre-projection sequence item, as built by dailySeqItems / labeledSeqItems
// and fed straight into recordSeqNavOptions.
export type SeqItem = {id: RecordSeqId; label: string | null};

export function toRecordSeq(items: (SeqSourceRow | null | undefined)[] | null | undefined): RecordSeqEntry[] {
  if (!Array.isArray(items)) return [];
  const out: RecordSeqEntry[] = [];
  for (const it of items) {
    if (!it || it.id == null) continue;
    const entry: RecordSeqEntry = {id: it.id, tag: it.tag == null ? null : it.tag};
    if (it.label != null) entry.label = it.label;
    out.push(entry);
  }
  return out;
}

// Build the navigate() options object that threads the visible-order sequence
// through route state. Use at the list row click and at Prev/Next.
export function recordSeqNavOptions(items: (SeqSourceRow | null | undefined)[] | null | undefined): {
  state: {recordSeq: RecordSeqEntry[]};
} {
  return {state: {recordSeq: toRecordSeq(items)}};
}

// Build sequence items for a daily-report list: label is the record date
// (mm/dd/yyyy) plus an optional " · <suffix>" (batch_label / herd / flock).
// Pass suffixField=null for date-only (egg daily). Mirrors each daily record
// page's title format (fmtMDY) so the neighbor label matches the destination
// title.
// `date` is called out of the index signature because it is the one column this
// function reads by a name it chose itself, and fmtMDY needs to know it is text.
export type DailySeqRow = SeqSourceRow & {date?: string | null};

export function dailySeqItems(
  rows: DailySeqRow[] | null | undefined,
  suffixField: string | null | undefined,
): SeqItem[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    const dateStr = r && r.date != null && r.date !== '' ? fmtMDY(r.date) : '';
    // Read once instead of twice — the suffix column is named by the caller, so
    // its value is `unknown` and only reaches the label through interpolation,
    // exactly as the `+` concatenation did.
    const suffixValue = suffixField && r ? r[suffixField] : null;
    const suffix = suffixValue ? ` · ${suffixValue}` : '';
    return {id: r.id, label: dateStr + suffix};
  });
}

// Build sequence items for a list whose label is a single record field
// (batch name, task title, equipment name, housing name, …). idField defaults
// to 'id' but can be overridden for routes keyed by another field (e.g.
// equipment routes by slug). Rows without the id field are dropped.
export function labeledSeqItems(
  rows: (SeqSourceRow | null | undefined)[] | null | undefined,
  labelField: string,
  idField = 'id',
): SeqItem[] {
  if (!Array.isArray(rows)) return [];
  const out: SeqItem[] = [];
  for (const r of rows) {
    if (!r || r[idField] == null) continue;
    out.push({id: r[idField], label: r[labelField] != null ? String(r[labelField]) : null});
  }
  return out;
}

// Pure neighbor lookup. Returns index -1 (caller hides the controls) when
// there is no reliable sequence: not an array, fewer than 2 entries, or the
// current id is not present in the sequence.
export type SequenceNeighbors = {
  index: number;
  total: number;
  prev: RecordSeqEntry | null;
  next: RecordSeqEntry | null;
};

export function findSequenceNeighbors(
  seq: RecordSeqEntry[] | null | undefined,
  currentId: RecordSeqId,
): SequenceNeighbors {
  const empty: SequenceNeighbors = {index: -1, total: 0, prev: null, next: null};
  if (!Array.isArray(seq) || seq.length < 2 || currentId == null) return empty;
  const cur = String(currentId);
  const index = seq.findIndex((it) => it && String(it.id) === cur);
  if (index === -1) return {index: -1, total: seq.length, prev: null, next: null};
  return {
    index,
    total: seq.length,
    prev: index > 0 ? seq[index - 1] : null,
    next: index < seq.length - 1 ? seq[index + 1] : null,
  };
}
